// Grid generator for matrix ("table") fields.
//
// Lives in its own module rather than in docOvelayTool so that the layout
// helpers can depend on it without a cycle: docOvelayTool now needs
// fillLayout -> builder/elements -> matrixItems, and elements previously
// imported this from docOvelayTool, which would have looped back.

import { columnWidthsOf, offsetsOf, rowHeightsOf } from './matrixGrid';

export interface MatrixTracks {
  // Width of each column / height of each row, in mm. Omit for a uniform grid
  // built from the gaps — that is what every table saved before per-column
  // sizing existed does.
  columnWidths?: number[];
  rowHeights?: number[];
}

export const createMatrixItems = (
  gapHorizontal,
  gapVertical,
  matrixTop,
  matrixLeft,
  rows,
  columns,
  text = 'data',
  tracks: MatrixTracks = {},
) => {
  const matrix = [];
  const ml = Number(matrixLeft);
  const mt = Number(matrixTop);
  const gh = Number(gapHorizontal);
  const gv = Number(gapVertical);
  const nRows = Number(rows);
  const nCols = Number(columns);

  // Cumulative offsets rather than `index * gap`, so columns may differ in
  // width. With no explicit widths every track is the gap and the output is
  // identical to the uniform grid this used to produce.
  const xs = offsetsOf(
    tracks.columnWidths && tracks.columnWidths.length
      ? tracks.columnWidths
      : columnWidthsOf({ columns: nCols, gapHorizontal: gh }),
  );
  const ys = offsetsOf(
    tracks.rowHeights && tracks.rowHeights.length
      ? tracks.rowHeights
      : rowHeightsOf({ rows: nRows, gapVertical: gv }),
  );

  for (let i = 0; i < nRows; i += 1) {
    const row = [];
    for (let j = 0; j < nCols; j += 1) {
      row.push({
        left: `${ml + (xs[j] ?? 0)}`,
        top: `${mt + (ys[i] ?? 0)}`,
        text,
        caption: '',
        name: `cell${i}${j}`,
      });
    }
    matrix.push(row);
  }
  return matrix;
};

/**
 * Rebuild a table's cells from the field itself.
 *
 * Preferred over calling createMatrixItems with eight positional arguments:
 * every caller that changes rows, columns, position or track sizes needs the
 * same read of the same six properties, and threading the new per-track sizes
 * through each call site by hand is how they drift apart.
 */
export const rebuildMatrix = (field: any, text = 'data') =>
  createMatrixItems(
    field.gapHorizontal,
    field.gapVertical,
    field.matrixTop,
    field.matrixLeft,
    field.rows,
    field.columns,
    text,
    { columnWidths: columnWidthsOf(field), rowHeights: rowHeightsOf(field) },
  );

export default createMatrixItems;
